import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import styled from 'styled-components';
import { useNavigate, Routes, Route, useParams, /*useLocation*/ } from 'react-router-dom'; // ✨ 라우팅 관련 훅 추가
import useAuth from '../hooks/useAuth';
import { getAllCardsForUser, Card, getCardById, updateCard, deleteCard } from '../api'; // ✨ API 추가
import Button from '../components/common/Button';
import CardDetailModal from '../components/board/CardDetailModal';
import ResponsiveHeader from "../components/common/ResponsiveHeader";

// ✨ [수정] 반응형 월 네비게이션 스타일
const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 1rem;

  /* 모바일: 전체 너비 사용 및 중앙 정렬 */
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin-right: 0;
    margin-bottom: 0.5rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border-color); /* 구분선 추가 */
  }
`;

const DateDisplay = styled.span`
  font-size: 1.1rem;
  font-weight: bold;
  color: var(--text-bright);
  min-width: 120px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    min-width: 100px;
  }
`;

// ✨ [수정] 반응형 필터 체크박스
const FilterCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-normal);
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;

  input { accent-color: var(--primary-accent); cursor: pointer; }
  &:hover { color: var(--text-bright); }

  /* 모바일: 터치 영역 확보를 위한 패딩 */
  @media (max-width: 768px) {
    padding: 0.25rem 0;
  }
`;

// --- 스타일 정의 ---
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg-body);
`;

const ChartContainer = styled.div`
  flex: 1;
  overflow: auto;
  padding: 1rem;
  position: relative;
  
  /* 스크롤바 커스텀 */
  &::-webkit-scrollbar { width: 8px; height: 8px; }
  &::-webkit-scrollbar-track { background: var(--bg-body); }
  &::-webkit-scrollbar-thumb { background: var(--text-dark); border-radius: 4px; }

  @media (max-width: 768px) {
    padding: 0.5rem; /* 모바일 패딩 축소 */
  }
`;

const GanttTable = styled.div`
  display: grid;
  /* 좌측: 업무명(200px), 우측: 타임라인(자동) */
  grid-template-columns: 200px 1fr;
  min-width: 1000px; /* 최소 너비 확보 */

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-width: auto; /* 내부 타임라인 길이에 맞춰지도록 자동 설정 */
  }
`;

const TaskColumnHeader = styled.div`
  background-color: var(--bg-list);
  color: var(--text-bright);
  padding: 0.75rem;
  font-weight: bold;
  border-bottom: 1px solid var(--border-color);
  border-right: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  left: 0;
  z-index: 30;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  background-color: var(--bg-list);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 20;
`;

const DateCell = styled.div`
  min-width: 40px; /* 하루 칸 너비 */
  padding: 0.5rem 0;
  text-align: center;
  border-right: 1px solid var(--border-color);
  color: var(--text-normal);
  font-size: 0.8rem;
  flex-shrink: 0;
  
  &.today {
    background-color: rgba(97, 175, 239, 0.1);
    color: var(--primary-accent);
    font-weight: bold;
  }
`;

const TaskRowName = styled.div`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--border-color);
  border-right: 1px solid var(--border-color);
  color: var(--text-bright);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: var(--bg-card);
  position: sticky;
  left: 0;
  z-index: 10;
  height: 40px;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TimelineRow = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  position: relative;
  height: 40px;
  background-color: var(--bg-card);
`;

const GridLine = styled.div`
  min-width: 40px;
  height: 100%;
  border-right: 1px solid var(--border-color);
  flex-shrink: 0;
  
  &.weekend {
    background-color: rgba(255, 255, 255, 0.02);
  }
  &.today {
    background-color: rgba(97, 175, 239, 0.05);
  }
`;

// 간트 바 스타일
const TaskBar = styled.div<{ width: number, offset: number, color: string }>`
  position: absolute;
  top: 8px; /* 중앙 정렬 */
  height: 24px;
  left: ${props => props.offset}px;
  width: ${props => props.width}px;
  background-color: ${props => props.color};
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: opacity 0.2s;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
  color: #000; /* 가독성을 위해 검정 텍스트 */
  font-size: 0.75rem;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    opacity: 0.8;
    z-index: 5;
  }
`;

// 유틸: 상태별 색상 (기존 코드와 통일)
const getStateColor = (state?: string) => {
    switch (state) {
        case 'IDLE': return '#FFD54F';
        case 'FOLLOWUP': return '#BA68C8';
        case 'COMPLETED': return '#81C784';
        case 'PROCESS': return '#64B5F6';
        default: return '#9e9e9e';
    }
};

// ✨ [신규] 상세 모달 라우트 래퍼 컴포넌트
const GanttCardModalWrapper: React.FC<{
    onClose: () => void,
    onUpdate: (card: Card) => void,
    onDelete: (id: string) => void
}> = ({ onClose, onUpdate, onDelete }) => {
    const { cardId } = useParams<{ cardId: string }>();
    const [card, setCard] = useState<Card | null>(null);
    const user = useAuth();
    const isReadOnly = card?.userId !== user?.id && card?.assigneeId !== user?.id; // 담당자는 진행을 고칠 수 있다

    useEffect(() => {
        const fetchCard = async () => {
            if (cardId) {
                try {
                    const response = await getCardById(cardId);
                    setCard(response.data);
                } catch (error) {
                    console.error("Failed to fetch card", error);
                    onClose();
                }
            }
        };
        fetchCard();
    }, [cardId, onClose]);

    if (!card) return null;

    return (
        <CardDetailModal
            card={card}
            onClose={onClose}
            onUpdate={(updatedCard) => {
                setCard(updatedCard);
                onUpdate(updatedCard);
            }}
            onDelete={() => {
                onDelete(card.id);
                onClose();
            }}
            onToggleComplete={async () => {
                const originalCard = { ...card };
                const updated = { ...card, completed: !card.completed };
                setCard(updated);
                try {
                    await updateCard({ id: card.id, completed: updated.completed });
                    onUpdate(updated);
                } catch(e: any) {
                    console.error(e);
                    setCard(originalCard);
                    const message = e.response?.data?.data || "완료 처리 실패";
                    alert(message);
                }
            }}
            fromBoard={false} // 간트차트에서는 특정 뷰 이동 버튼 필요성에 따라 조정 가능
            readOnly={isReadOnly}
        />
    );
};

const GanttChartPage = () => {
    useAuth();
    const navigate = useNavigate();
    // const location = useLocation();
    const [cards, setCards] = useState<Card[]>([]);

    // 차트 날짜 범위 설정 (오늘 기준 앞뒤 15일, 혹은 데이터 기반)
    // 실제로는 데이터의 min/max 날짜를 구해서 동적으로 하는 것이 좋음
    const [showCompleted, setShowCompleted] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    // ✨ [신규] 무한 스크롤을 위한 관찰자(Observer)
    const observer = useRef<IntersectionObserver | null>(null);
    const lastElementRef = useCallback((node: HTMLDivElement | null) => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    // 현재 월의 1일과 마지막 날 계산
    const { startDate, daysInMonth } = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const start = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        return { startDate: start, daysInMonth: lastDay.getDate() };
    }, [currentDate]);

    // ✨ [변경] 월 이동 핸들러
    const handlePrevMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const fetchCards = useCallback(async (pageParam: number) => {
        setIsLoading(true);
        try {
            // API에 전달할 날짜 포맷 (YYYY-MM-DD HH:mm:ss)
            // 시작일: 해당 월 1일 00:00:00
            const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')} 00:00:00.000`;
            // 종료일: 해당 월 말일 23:59:59
            const lastDay = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
            const endStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')} 23:59:59.000`;

            // 검색 조건: 기간 내에 있는 업무를 조회하기 위해 범위 전달
            // (서버 API가 startDate, dueDate 파라미터를 지원한다고 가정)
            const searchParams = {
                startDate: startStr,
                dueDate: endStr
            };

            const response = await getAllCardsForUser(
                pageParam,
                20, // 한 번에 가져올 개수
                ['startDate,asc', 'dueDate,asc'],
                searchParams
            );

            const newCards = response.data._embedded?.getList || [];

            setCards(prev => {
                if (pageParam === 0) return newCards;
                // 중복 제거 후 추가
                const existingIds = new Set(prev.map(c => c.id));
                return [...prev, ...newCards.filter(c => !existingIds.has(c.id))];
            });

            // 가져온 데이터가 요청 사이즈(20)보다 작으면 더 이상 데이터가 없음
            if (newCards.length < 20) {
                setHasMore(false);
            }
        } catch (e) {
            console.error(e);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    }, [startDate]);

    // ✨ [변경] 월(StartDate)이 바뀌면 목록 초기화 및 첫 페이지 조회
    useEffect(() => {
        setPage(0);
        setHasMore(true);
        setCards([]); // 이전 달 데이터 비우기
        fetchCards(0);
    }, [startDate, fetchCards]);

    // ✨ [신규] 페이지 번호가 증가하면 추가 조회
    useEffect(() => {
        if (page > 0) {
            fetchCards(page);
        }
    }, [page, fetchCards]);

    // useEffect(() => {
    //     // 날짜 범위 시작점을 10일 전으로 설정
    //     const d = new Date();
    //     d.setDate(d.getDate() - 10);
    //
    //     const fetchAllCards = async () => {
    //         try {
    //             // 100개 정도 넉넉히 조회 (실제로는 페이지네이션 고려 필요)
    //             const response = await getAllCardsForUser(0, 100, ['startDate,asc','dueDate,asc']);
    //             setCards(response.data._embedded?.getList || []);
    //         } catch (e) {
    //             console.error(e);
    //         }
    //     };
    //     fetchAllCards();
    // }, []);

    // 타임라인에 표시할 날짜 배열 생성
    const timelineDates = useMemo(() => {
        const dates = [];
        for (let i = 0; i < daysInMonth; i++) {
            const d = new Date(startDate);
            d.setDate(d.getDate() + i);
            dates.push(d);
        }
        return dates;
    }, [startDate, daysInMonth]);

    // 카드별 위치(offset) 및 너비(width) 계산
    const getTaskPosition = (card: Card) => {
        if (!card.startDate || !card.dueDate) return null;

        const taskStart = new Date(card.startDate);
        const taskEnd = new Date(card.dueDate);

        // 차트의 시작일(1일 00:00)과 끝일(말일)
        const chartStart = timelineDates[0];
        const chartEnd = timelineDates[timelineDates.length - 1];

        // 1. 차트 범위와 아예 겹치지 않으면 렌더링 안 함
        // (업무 끝이 차트 시작보다 전이거나, 업무 시작이 차트 끝보다 후인 경우)
        if (taskEnd < chartStart || taskStart > chartEnd) return null;

        // 2. 보여질 시작일과 종료일 계산 (Clipping)
        // 업무 시작일이 차트 시작일보다 빠르면 차트 시작일로 자름
        const visibleStart = taskStart < chartStart ? chartStart : taskStart;

        // 업무 종료일이 차트 종료일보다 늦으면 차트 종료일로 자름
        // (주의: 날짜 비교 시 시간을 고려해야 하므로, 간단히 해당 일자의 범위를 넘어가는지로 판단)
        let visibleEnd = taskEnd;
        // chartEnd는 해당 일의 00:00:00 기준이므로, 날짜가 더 크면 확실히 넘어간 것
        if (taskEnd > chartEnd) {
            visibleEnd = chartEnd;
        }

        // 3. 오프셋 계산 (시작점 위치)
        const diffTime = visibleStart.getTime() - chartStart.getTime();
        // 음수가 나올 경우 0으로 보정 (이미 위에서 visibleStart 보정했지만 안전장치)
        const diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));

        // 4. 너비 계산 (기간)
        const durationTime = visibleEnd.getTime() - visibleStart.getTime();
        // 최소 1일 보장
        const durationDays = Math.max(1, Math.floor(durationTime / (1000 * 60 * 60 * 24)) + 1);

        const CELL_WIDTH = 40; // 40px 기준
        const offset = diffDays * CELL_WIDTH;
        const width = durationDays * CELL_WIDTH;

        return { offset, width };
    };

    const isWeekend = (date: Date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const formatDateHeader = (date: Date) => {
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    // ✨ [신규] 필터링 및 정렬된 카드 목록 계산
    const displayedCards = useMemo(() => {
        let result = cards;

        if (!showCompleted) {
            result = result.filter(c => !c.completed);
        }

        // startDate 오름차순, 같으면 dueDate 오름차순 정렬
        result.sort((a, b) => {
            const startA = a.startDate ? new Date(a.startDate).getTime() : 0;
            const startB = b.startDate ? new Date(b.startDate).getTime() : 0;

            if (startA !== startB) {
                return startA - startB;
            }

            const endA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
            const endB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
            return endA - endB;
        });

        return result.filter(c => c.startDate && c.dueDate);
    }, [cards, showCompleted]);

    // ✨ [신규] 모달 관련 핸들러
    const handleCardUpdate = (updatedCard: Card) => {
        setCards(prev => prev.map(c => c.id === updatedCard?.id ? updatedCard : c));
    };

    const handleCardDelete = async (cardId: string) => {
        await deleteCard(cardId);
        setCards(prev => prev.filter(c => c.id !== cardId));
        navigate('/gantt');
    };

    const handleCloseModal = () => {
        navigate('/gantt');
    };

    return (
        <PageWrapper>
            <ResponsiveHeader
                title="📅 Gantt Chart"
                leftContent={
                    <>
                        <MonthNav>
                            <Button onClick={handlePrevMonth}>&lt;</Button>
                            <DateDisplay>
                                {currentDate.getFullYear()}. {currentDate.getMonth() + 1}
                            </DateDisplay>
                            <Button onClick={handleNextMonth}>&gt;</Button>
                            <Button onClick={handleToday} style={{fontSize: '0.8rem', padding: '0.2rem 0.5rem'}}>
                                Today
                            </Button>
                        </MonthNav>
                        <FilterCheckbox>
                            <input
                                type="checkbox"
                                checked={showCompleted}
                                onChange={(e) => setShowCompleted(e.target.checked)}
                            />
                            완료 업무 표시
                        </FilterCheckbox>
                    </>
                }
            />

            <ChartContainer>
                <GanttTable>
                    <TaskColumnHeader>Task</TaskColumnHeader>
                    <TimelineHeader>
                        {timelineDates.map((date, idx) => (
                            <DateCell key={idx} className={isToday(date) ? 'today' : ''}>
                                {formatDateHeader(date)}
                            </DateCell>
                        ))}
                    </TimelineHeader>

                    {displayedCards.map(card => {
                        const pos = getTaskPosition(card);
                        // pos가 null이면(범위 밖) 렌더링 하지 않음
                        if (!pos) return null;

                        return (
                            <React.Fragment key={card.id}>
                                <TaskRowName title={card.title}>{card.title}</TaskRowName>
                                <TimelineRow>
                                    {timelineDates.map((date, idx) => (
                                        <GridLine
                                            key={idx}
                                            className={`${isWeekend(date) ? 'weekend' : ''} ${isToday(date) ? 'today' : ''}`}
                                        />
                                    ))}
                                    <TaskBar
                                        width={pos.width}
                                        offset={pos.offset}
                                        color={getStateColor(card.state)}
                                        onClick={() => navigate(`/gantt/card/${card.id}`)}
                                        title={`${card.title} (${card.startDate?.split(' ')[0]} ~ ${card.dueDate?.split(' ')[0]})`}
                                    >
                                        {card.title}
                                    </TaskBar>
                                </TimelineRow>
                            </React.Fragment>
                        );
                    })}
                    <div ref={lastElementRef} style={{ gridColumn: '1 / -1', height: '20px', backgroundColor: 'transparent' }}>
                        {isLoading && <span style={{marginLeft: '10px', fontSize: '0.8rem', color: 'var(--text-dark)'}}>Loading...</span>}
                    </div>
                </GanttTable>
            </ChartContainer>

            <Routes>
                <Route
                    path="card/:cardId"
                    element={
                        <GanttCardModalWrapper
                            onClose={handleCloseModal}
                            onUpdate={handleCardUpdate}
                            onDelete={handleCardDelete}
                        />
                    }
                />
            </Routes>
        </PageWrapper>
    );
};

export default GanttChartPage;